import { ChangeEvent, MouseEvent, useEffect, useMemo, useRef, useState } from 'react';
import styled from '@emotion/styled';
import {
  alpha,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Radio,
  RadioGroup,
  TextField
} from '@mui/material';

import { PolarisIcon } from '@/exports/columnCommonComponents';
import FilterAccordion from '@/components/_common/Filters/FilterAccordion';
import Tooltip from '@/components/_common/Tooltip';

export type AnyAllFilterMatchType = 'filter' | 'contains';

type FilterMultiSelectProps = {
  type: string;
  label: string;
  options: Record<string, string> | string[];
  selectedOptions: string[];
  expandedFilters: string[];
  matchType?: AnyAllFilterMatchType;
  onListItemClick: (e: MouseEvent) => void;
  onSelectAll: (newValues: Record<string, string> | string[] | undefined) => void;
  onClear: (e: MouseEvent) => void;
  onExpand: (filter: string) => void;
  onMatchTypeChange?: (value: AnyAllFilterMatchType, type: string) => void;
  isAnyAllEnabled?: boolean;
};

const FilterMultiSelect = ({
  type,
  label,
  options,
  selectedOptions,
  expandedFilters,
  matchType,
  onListItemClick,
  onSelectAll,
  onClear,
  onExpand,
  isAnyAllEnabled,
  onMatchTypeChange = () => {}
}: FilterMultiSelectProps) => {
  const [searchInput, setSearchInput] = useState('');
  const [filteredOptions, setFilteredOptions] = useState(options);
  const [selectedFiltersExpanded, setSelectedFiltersExpanded] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const optionsIsArray = options.constructor === Array;
  const allOptions = optionsIsArray ? options : Object.keys(options);
  const isSelectedAll = allOptions.length > 0 && allOptions.every((opt) => selectedOptions?.includes(opt));
  const isIndeterminate = !isSelectedAll && allOptions.some((opt) => selectedOptions?.includes(opt));
  const showClearButton = selectedOptions?.length > 0;
  const isAccordionExpanded = expandedFilters.includes(type);

  const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchInput(e.target.value);
  };

  const handleSelectAll = () => {
    onSelectAll(searchInput ? filteredOptions : undefined);
  };

  const handleClearSearchButtonClick = () => {
    setSearchInput('');
  };

  const handleExpand = () => {
    setSelectedFiltersExpanded(true);
  };

  const handleCollapse = () => {
    setSelectedFiltersExpanded(false);
  };

  const handleMatchTypeChange = (e: ChangeEvent<HTMLInputElement>) => {
    onMatchTypeChange(e.target.value as 'filter' | 'contains', type);
  };

  const handleClear = (e: MouseEvent) => {
    onClear(e);
    setSearchInput('');
    setSelectedFiltersExpanded(false);
  };

  const sortedOptions = Object.entries(filteredOptions).sort((a, b) => {
    if (a[1] === 'None' && b[1] !== 'None') return 1;
    if (b[1] === 'None' && a[1] !== 'None') return -1;
    return a[1].localeCompare(b[1]);
  });

  const chipsToDisplay = useMemo(() => {
    if (selectedFiltersExpanded) return { count: selectedOptions.length, characterLength: 0 };

    let count = 0;
    let characterLength = 0;

    for (const curr of selectedOptions) {
      const label = optionsIsArray ? curr : (options as Record<string, string>)[curr];
      const nextCount = count + 1;
      const nextCharacterLength = characterLength + label?.length;
      if (nextCharacterLength > 30 || nextCount > 3) {
        break;
      }

      count = nextCount;
      characterLength = nextCharacterLength;
    }

    return { count, characterLength };
  }, [selectedOptions, selectedFiltersExpanded]);

  useEffect(
    function filterOptionsOnSearchInputChange() {
      const searchValue = searchInput.toLowerCase();

      if (optionsIsArray) {
        const filtered = options.filter((value) => value.toLowerCase().includes(searchValue));
        setFilteredOptions(filtered);
      } else {
        const filtered = Object.fromEntries(
          Object.entries(options).filter(([key, value]) => key.toLowerCase().includes(searchValue) || value.toLowerCase().includes(searchValue))
        );
        setFilteredOptions(filtered);
      }
    },
    [searchInput, options]
  );

  useEffect(
    function collapseFilterOnFilterRemove() {
      if (selectedOptions.length === 0 && selectedFiltersExpanded) {
        setSelectedFiltersExpanded(false);
      }
    },
    [selectedOptions, selectedFiltersExpanded]
  );

  return (
    <FilterAccordion
      expanded={isAccordionExpanded}
      label={label}
      count={selectedOptions.length}
      onClear={handleClear}
      showClear={showClearButton}
      focusElementOnExpand={inputRef}
      onChange={() => onExpand(type)}
    >
      <StyledBox>
        <SelectedFiltersContainer>
          {selectedOptions.slice(0, chipsToDisplay.count).map((so) => (
            <SelectedFilterChip
              id={so}
              key={so}
              label={optionsIsArray ? so : (options as Record<string, string>)[so]}
              deleteIcon={<PolarisIcon id={so} icon="close" height={16} />}
              onClick={onListItemClick}
              onDelete={onListItemClick}
            />
          ))}
          {selectedOptions.length > chipsToDisplay.count && !selectedFiltersExpanded && (
            <Tooltip title="Show more">
              <ExpandButton variant="text" onClick={handleExpand}>
                ...
              </ExpandButton>
            </Tooltip>
          )}
          {selectedOptions.length > 0 && <SelectedOptionsCount>{selectedOptions.length}</SelectedOptionsCount>}
          {selectedFiltersExpanded && (
            <CollapseWrapper>
              <CollapseButton
                variant="text"
                size="small"
                color="default"
                startIcon={<PolarisIcon icon="subtract" height={16} width={8} />}
                onClick={handleCollapse}
              >
                Show less
              </CollapseButton>
            </CollapseWrapper>
          )}
        </SelectedFiltersContainer>
        <SearchField
          fullWidth
          value={searchInput}
          label={`Search ${label}`}
          inputRef={inputRef}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                {searchInput.length > 0 ? (
                  <ClearSearchButton onClick={handleClearSearchButtonClick}>
                    <PolarisIcon icon="close_filled" />
                  </ClearSearchButton>
                ) : (
                  <PolarisIcon icon="search" />
                )}
              </InputAdornment>
            )
          }}
          onChange={handleSearchChange}
        />
        <ControlsGrid container alignItems="center" justifyContent={isAnyAllEnabled ? 'space-between' : 'flex-end'} py={1}>
          {isAnyAllEnabled && (
            <RadioGroup row value={matchType} onChange={handleMatchTypeChange}>
              <MatchTypeLabel value="filter" control={<Radio />} label="Match Any" />
              <MatchTypeLabel value="contains" control={<Radio />} label="Match All" />
            </RadioGroup>
          )}
          {!isSelectedAll && (
            <SelectAll
              value="select-all"
              control={<Checkbox indeterminate={isIndeterminate} checked={isSelectedAll} onChange={handleSelectAll} />}
              label="Select All"
              labelPlacement="start"
            />
          )}
        </ControlsGrid>
        <StyledList>
          {sortedOptions.map(([value, key], index) => {
            const realValue = optionsIsArray ? key : value;
            return (
              <ListItem key={key} tabIndex={index + 1} disablePadding>
                <StyledListItemButton dense id={realValue} selected={selectedOptions.includes(realValue)} onClick={onListItemClick}>
                  <ListItemText primary={key} />
                  <StyledListItemIcon>
                    <Checkbox edge="start" checked={selectedOptions.includes(realValue)} />
                  </StyledListItemIcon>
                </StyledListItemButton>
              </ListItem>
            );
          })}
        </StyledList>
      </StyledBox>
    </FilterAccordion>
  );
};

export default FilterMultiSelect;

export const StyledBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  padding: theme.spacing(1.5),
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius
}));

export const SelectedFiltersContainer = styled(Grid)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: theme.spacing(1),
  paddingBottom: theme.spacing(1),
  alignItems: 'center'
}));

export const SelectedFilterChip = styled(Chip)(({ theme }) => ({
  padding: theme.spacing(0.25, 0.5),
  gap: theme.spacing(0.5),
  color: theme.palette.primary.main,
  fontSize: theme.typography.caption.fontSize,
  fontWeight: theme.typography.fontWeightBold,
  backgroundColor: alpha(theme.palette.primary.main, 0.16),
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.32)
  },
  '& .MuiChip-label': {
    padding: theme.spacing(0, 0.5)
  },
  '& .MuiChip-deleteIcon': {
    padding: 0,
    width: 12,
    height: 12,
    borderRadius: 100,
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    margin: theme.spacing(0, 0.5, 0, 0),
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: theme.palette.primary.contrastText,
      color: theme.palette.primary.main
    }
  }
}));

export const SelectedOptionsCount = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  fontSize: '0.75rem',
  paddingLeft: 'auto',
  borderRadius: 12,
  height: 'fit-content',
  width: 'fit-content',
  padding: theme.spacing(0.25, 1.5),
  lineHeight: 2,
  marginLeft: 'auto'
}));

export const SearchField = styled(TextField)(({ theme }) => ({
  marginTop: theme.spacing(1),
  '& .MuiOutlinedInput-root': {
    backgroundColor: theme.palette?.filters?.input?.background || (theme.palette.mode === 'dark' ? '#25272C' : '#f5f5f5'),
    '&.Mui-focused, &:hover': {
      backgroundColor: theme.palette?.filters?.input?.focused || (theme.palette.mode === 'dark' ? '#313339' : '#ffffff')
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette?.filters?.input?.border || (theme.palette.mode === 'dark' ? '#313339' : '#a4a4a4')
    }
  },
  '& .MuiInputAdornment-root': {
    color: theme.palette.primary.main
  }
}));

const SelectAll = styled(FormControlLabel)(({ theme }) => ({
  marginLeft: 0,
  marginRight: 0,
  '& .MuiFormControlLabel-label': {
    fontSize: theme.typography.caption.fontSize,
    fontWeight: 600
  }
}));

const MatchTypeLabel = styled(FormControlLabel)(() => ({
  marginLeft: 0,
  marginRight: 0
}));

const StyledList = styled(List)(({ theme }) => ({
  overflow: 'auto',
  maxHeight: 200,
  padding: 0,
  '& li:not(:last-of-type)': {
    marginBottom: theme.spacing(0.5)
  }
}));

const StyledListItemButton = styled(ListItemButton)(({ theme }) => ({
  padding: theme.spacing(0.5, 0, 0.5, 1)
}));

const StyledListItemIcon = styled(ListItemIcon)(() => ({
  minWidth: 'auto'
}));

export const ClearSearchButton = styled(IconButton)(({ theme }) => ({
  padding: 0,
  color: theme.palette.action.disabled,
  transition: 'all 0.3s ease'
}));

export const ExpandButton = styled(Button)(({ theme }) => ({
  fontSize: theme.typography.caption.fontSize,
  minWidth: 'fit-content'
}));

export const CollapseWrapper = styled.div(({ theme }) => ({
  flexBasis: '100%',
  marginTop: theme.spacing(1)
}));

export const CollapseButton = styled(Button)(() => ({
  width: 'fit-content'
}));

const ControlsGrid = styled(Grid)(() => ({
  '& .MuiFormControlLabel-label': {
    fontSize: '0.875rem'
  }
}));
